import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';

const AutocompleteContainer = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background-color: var(--clr-prim);
  border-radius: 8px 8px 0 0;
  border: 1px solid var(--clr-tert);
  border-bottom: none;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
`;

const AutocompleteItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  background-color: ${props => props.$selected ? 'var(--clr-tert)' : 'transparent'};
  border-bottom: 1px solid var(--clr-tert);
  
  &:hover {
    background-color: var(--clr-tert);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const UserColorDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.$color || 'var(--clr-sec)'};
  flex-shrink: 0;
`;

const UserNickname = styled.span`
  color: var(--clr-sec);
  font-size: 14px;
  font-weight: 500;
`;

const UserInfo = styled.span`
  color: var(--clr-sec);
  font-size: 12px;
  opacity: 0.7;
`;

function MentionAutocomplete({ 
  inputText, 
  cursorPosition, 
  messages, 
  onSelectUser, 
  onClose 
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState(-1);
  const containerRef = useRef();

  // Memoize user extraction to avoid recalculating on every render
  const extractedUsers = useMemo(() => {
    const userMap = new Map();
    
    messages.forEach(message => {
      if (message.nickname && message.userId !== 'self') {
        const key = message.nickname.toLowerCase();
        if (!userMap.has(key)) {
          userMap.set(key, {
            nickname: message.nickname,
            color: message.color,
            age: message.age,
            pronouns: message.pronouns,
            userId: message.userId
          });
        }
      }
    });
    
    return Array.from(userMap.values());
  }, [messages]);

  // Detect @ mention and extract query
  useEffect(() => {
    const text = inputText.slice(0, cursorPosition);
    const lastAtIndex = text.lastIndexOf('@');
    
    if (lastAtIndex === -1) {
      setMentionStart(-1);
      setSuggestions([]);
      return;
    }
    
    // Check if there's a space between @ and cursor (which would end the mention)
    const textAfterAt = text.slice(lastAtIndex + 1);
    if (textAfterAt.includes(' ')) {
      setMentionStart(-1);
      setSuggestions([]);
      return;
    }
    
    // Valid mention in progress
    setMentionStart(lastAtIndex);
    setMentionQuery(textAfterAt.toLowerCase());
    
    // Filter users by query (using memoized users)
    const filtered = extractedUsers.filter(user => 
      user.nickname.toLowerCase().includes(textAfterAt.toLowerCase())
    );
    
    setSuggestions(filtered.slice(0, 8)); // Limit to 8 suggestions
    setSelectedIndex(0);
  }, [inputText, cursorPosition, extractedUsers]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (suggestions.length === 0) return;
      
      // Only handle these keys if the target is an input element and autocomplete is active
      if (e.target.tagName !== 'INPUT') return;
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
        case 'Enter':
        case 'Tab':
          if (suggestions[selectedIndex]) {
            e.preventDefault();
            handleSelectUser(suggestions[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    if (suggestions.length > 0) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [suggestions, selectedIndex, onClose]);

  const handleSelectUser = (user) => {
    onSelectUser(user, mentionStart, mentionQuery.length + 1); // +1 for the @ symbol
  };

  if (suggestions.length === 0) return null;

  return (
    <AutocompleteContainer ref={containerRef}>
      {suggestions.map((user, index) => (
        <AutocompleteItem
          key={`${user.userId}-${user.nickname}`}
          $selected={index === selectedIndex}
          onClick={() => handleSelectUser(user)}
        >
          <UserColorDot $color={user.color} />
          <div style={{ flex: 1 }}>
            <UserNickname>@{user.nickname}</UserNickname>
            {(user.age || user.pronouns) && (
              <UserInfo>
                {' · '}
                {[user.age, user.pronouns].filter(Boolean).join(' · ')}
              </UserInfo>
            )}
          </div>
        </AutocompleteItem>
      ))}
    </AutocompleteContainer>
  );
}

export default MentionAutocomplete;